/**
 * Logger utility that sends logs to file via log server
 * Falls back to console if server is not available
 */

class Logger {
  constructor() {
    this.logs = [];
    this.logServerUrl = 'http://localhost:3001/log';
    this.batchSize = 10;
    this.batchTimeout = 100; // 100ms for faster logging
    this.pendingLogs = [];
    this.batchTimer = null;
  }

  log(level, category, message, data = {}) {
    const logEntry = {
      level,
      category,
      message,
      data,
      timestamp: new Date().toISOString()
    };

    // Always log to console
    const consoleMethod = level === 'error' ? console.error : 
                         level === 'warn' ? console.warn : 
                         console.log;
    consoleMethod(`[${category}] ${message}`, data);

    // Add to pending logs
    this.pendingLogs.push(logEntry);
    this.logs.push(logEntry);

    // Batch and send logs
    this.scheduleBatchSend();
  }

  scheduleBatchSend() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    this.batchTimer = setTimeout(() => {
      this.sendBatch();
    }, this.batchTimeout);
  }

  async sendBatch() {
    if (this.pendingLogs.length === 0) return;

    const logsToSend = this.pendingLogs.splice(0, this.batchSize);

    try {
      const response = await fetch(this.logServerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          logs: logsToSend
        })
      });
      if (!response.ok) {
        console.warn('Log server returned error:', response.status);
      }
    } catch (error) {
      // Silently fail - server might not be running
      // Logs are still in this.logs for manual export
      console.warn('Failed to send logs to server:', error.message);
    }
  }

  // Force send all pending logs immediately
  async flush() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    await this.sendBatch();
    // Send any remaining logs
    if (this.pendingLogs.length > 0) {
      await this.sendBatch();
    }
  }

  // Export all logs as JSON (for manual download)
  exportLogs() {
    return JSON.stringify(this.logs, null, 2);
  }

  // Download logs as file
  downloadLogs() {
    const blob = new Blob([this.exportLogs()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fan-graph-logs-${new Date().toISOString().replace(/:/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Clear logs
  clear() {
    this.logs = [];
    this.pendingLogs = [];
  }
}

// Create singleton instance
const logger = new Logger();

// Export convenience methods
export const log = (category, message, data) => logger.log('info', category, message, data);
export const logError = (category, message, data) => logger.log('error', category, message, data);
export const logWarn = (category, message, data) => logger.log('warn', category, message, data);
export const exportLogs = () => logger.exportLogs();
export const downloadLogs = () => logger.downloadLogs();
export const clearLogs = () => logger.clear();
export const flushLogs = () => logger.flush();

export default logger;

